import { useEffect, useRef, useState } from 'react';
import { getSpineManager } from '../../lib/spine-manager';
import { useSceneStore } from '../../stores/sceneStore';
import { api } from '../../lib/api-client';
import type { Layer, Asset } from '@spine-workspace/shared';

interface ImageLayerRendererProps {
  layer: Layer;
}

/**
 * Renders a single image layer in the PixiJS scene.
 * Fetches a fresh image URL from the API (same pattern as SpineLayerRenderer)
 * so the stored layer data only needs the assetId, not a potentially-stale URL.
 */
export default function ImageLayerRenderer({ layer }: ImageLayerRendererProps) {
  const loaded = useRef(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    async function load() {
      const manager = getSpineManager();
      await manager.waitForInit();
      if (cancelled) return;

      // Resolve the image URL: prefer layer.imageUrl if already set,
      // otherwise fetch a fresh URL from the API.
      let imageUrl = layer.imageUrl;
      if (!imageUrl) {
        try {
          const res = await api.get<{ asset: Asset }>(`/api/assets/${layer.assetId}`);
          imageUrl = res.asset.jsonUrl;
        } catch (err) {
          console.error('[ImageLayerRenderer] Failed to resolve image URL for', layer.assetId, err);
          setIsLoading(false);
          return;
        }
      }
      if (!imageUrl) {
        console.error('[ImageLayerRenderer] No image URL for layer', layer.id);
        setIsLoading(false);
        return;
      }
      if (cancelled) return;

      try {
        await manager.loadImageAsset(layer.assetId, imageUrl);
      } catch (err) {
        console.error('[ImageLayerRenderer] Failed to load image asset', layer.assetId, err);
        setIsLoading(false);
        return;
      }
      if (cancelled) return;

      const sprite = manager.addImageToScene(layer.id, layer.assetId, layer.transform);
      if (!sprite) {
        console.error('[ImageLayerRenderer] addImageToScene returned null for layer', layer.id);
        setIsLoading(false);
        return;
      }

      loaded.current = true;
      setIsLoading(false);
      manager.setImageVisible(layer.id, layer.visible);
      manager.setImageOpacity(layer.id, layer.opacity);
      if (layer.blendMode && layer.blendMode !== 'normal') {
        manager.setImageBlendMode(layer.id, layer.blendMode);
      }
      if (layer.effects) {
        manager.setLayerEffects(layer.id, layer.effects.brightness, layer.effects.saturation, layer.effects.tint);
      }
    }

    load();

    return () => {
      cancelled = true;
      if (loaded.current) {
        const { assetId } = layer;
        getSpineManager().removeImageFromScene(layer.id);
        // Unload the texture only if no other scene layer still references this asset.
        const remainingLayers = useSceneStore.getState().layers;
        const stillUsed = remainingLayers.some((l) => l.assetId === assetId);
        if (!stillUsed) {
          getSpineManager().unloadImageAsset(assetId);
        }
        loaded.current = false;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer.id, layer.assetId]);

  useEffect(() => {
    if (!loaded.current) return;
    getSpineManager().updateImageTransform(layer.id, layer.transform);
  }, [layer.id, layer.transform]);

  useEffect(() => {
    if (!loaded.current) return;
    getSpineManager().setImageVisible(layer.id, layer.visible);
  }, [layer.id, layer.visible]);

  useEffect(() => {
    if (!loaded.current) return;
    getSpineManager().setImageOpacity(layer.id, layer.opacity);
  }, [layer.id, layer.opacity]);

  useEffect(() => {
    if (!loaded.current) return;
    getSpineManager().setImageBlendMode(layer.id, layer.blendMode ?? 'normal');
  }, [layer.id, layer.blendMode]);

  useEffect(() => {
    if (!loaded.current) return;
    const e = layer.effects;
    getSpineManager().setLayerEffects(
      layer.id,
      e?.brightness ?? 1,
      e?.saturation ?? 0,
      e?.tint ?? null,
    );
  }, [layer.id, layer.effects]);

  if (!isLoading) return null;

  return (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none flex items-center gap-2 bg-black/50 rounded-lg px-3 py-2">
      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      <span className="text-xs text-white/70">Loading…</span>
    </div>
  );
}
